import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card, Chip, Button, Avatar, Skeleton } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";
import test from "../../assets/images/test.png";
import exam from "../../assets/images/exam.png";
import interviewIcon from "../../assets/images/mental.png";
import { cardSx, commonAvatarStyle, initials, SectionTitle, CardTitle, KpiCard, showApiError } from "./CorporateDashboard";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";

// Corporate Candidate (STUDENT) dashboard — GET /student/dashboard/summary; same building blocks and style as CorporateDashboard

// Same list height as the Corporate dashboard cards
const LIST_HEIGHT = 358;

// Light-blue date pill used by the corporate list cards
const datePillSx = {
  px: 1.5,
  py: 0.25,
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 700,
  whiteSpace: "nowrap",
  flexShrink: 0,
  backgroundColor: "#e3f2fd",
  color: "#1565c0",
};

// attempt_status -> status chip
const ATTEMPT_CHIP = {
  COMPLETED: { label: "Completed", color: "success" },
  IN_PROGRESS: { label: "In progress", color: "warning" },
};
const NOT_STARTED_CHIP = { label: "Not started", color: "primary" };

// Interview status -> chip colour (same colours as the corporate interview chips)
const interviewChipColor = (status = "") =>
  ({ RESCHEDULED: "warning", COMPLETED: "success", CANCELLED: "error", DECLINED: "error", CALLED_OFF: "default" })[
    String(status).toUpperCase()
  ] || "primary";

const formatStatus = (status = "") =>
  String(status).toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Meeting links may come without a protocol ("gmeet.com/...")
const meetingHref = (url = "") => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const RowsSkeleton = () =>
  [0, 1, 2, 3].map((row) => <Skeleton key={row} variant="rounded" height={58} sx={{ mb: 1.5 }} />);

// Row look shared by both lists (thin divider + hover like the corporate cards)
const rowSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  mb: 1.5,
  pb: 1,
  borderBottom: "1px solid #f2f2f2",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#f9fafc", borderRadius: 2, pl: 1 },
};

/* ---------------- Sections ---------------- */

// my_assessments: name · date pill / time · duration · status. Starting happens on the Assessments page
// (it has the proctoring details the test screen needs).
const MyAssessments = ({ data, loading, examLabel, onOpenAssessments, onOpenResults }) => (
  <Card sx={{ ...cardSx, p: 2.5, flex: { xs: "1 1 100%", lg: "1 1 0" }, minWidth: 0, display: "flex", flexDirection: "column" }}>
    <CardTitle title={`My ${examLabel}s`} meta={loading ? "" : `${data.length} Total`} />
    <Box sx={{ height: LIST_HEIGHT, overflowY: "auto", overflowX: "hidden" }}>
      {loading ? (
        <RowsSkeleton />
      ) : data.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <TableNodata message={`No ${examLabel}s`} description={`You have no ${examLabel.toLowerCase()}s assigned yet.`} />
        </Box>
      ) : (
        data.map((item) => {
          const chip = ATTEMPT_CHIP[item.attempt_status] || NOT_STARTED_CHIP;
          const completed = item.attempt_status === "COMPLETED";
          const scheduledAt =
            item.scheduled_date && item.scheduled_time ? dayjs(`${item.scheduled_date}T${item.scheduled_time}`) : null;
          return (
            <Box key={item.assessment_id} sx={rowSx}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Name + date pill */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <TruncatedText onlyWhenTruncated text={item.assessment_name} sx={{ fontWeight: 600, fontSize: "14px" }} />
                  {scheduledAt?.isValid() && <Box sx={datePillSx}>{scheduledAt.format("MMMM D")}</Box>}
                </Box>
                {/* Time · duration + status / action */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", minWidth: 0 }} noWrap>
                    {scheduledAt?.isValid() && (
                      <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                        {scheduledAt.format("hh:mm A")}
                      </Box>
                    )}
                    {item.duration ? ` · ${item.duration} Minutes` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Chip label={chip.label} size="small" color={chip.color} variant="outlined" sx={{ height: 20, fontSize: "11px", fontWeight: 600 }} />
                    <Button
                      size="small"
                      variant={completed ? "outlined" : "contained"}
                      onClick={completed ? onOpenResults : onOpenAssessments}
                      sx={{ textTransform: "none", fontWeight: 600, minWidth: 88, height: 26 }}
                    >
                      {completed ? "View result" : item.attempt_status === "IN_PROGRESS" ? "Resume" : "Open"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
    <Box sx={{ pt: 1.5, borderTop: "1px solid #f2f2f2" }}>
      <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onOpenAssessments} sx={{ textTransform: "none", fontWeight: 600, px: 0 }}>
        View all {examLabel.toLowerCase()}s
      </Button>
    </Box>
  </Card>
);

// my_interviews: interviewer · date pill / job role / time · duration · status + Join meeting
const MyInterviews = ({ data, loading }) => (
  <Card sx={{ ...cardSx, p: 2.5, flex: { xs: "1 1 100%", lg: "1 1 0" }, minWidth: 0, display: "flex", flexDirection: "column" }}>
    <CardTitle title="My Interviews" meta={loading ? "" : `${data.length} Scheduled`} />
    <Box sx={{ height: LIST_HEIGHT, overflowY: "auto", overflowX: "hidden" }}>
      {loading ? (
        <RowsSkeleton />
      ) : data.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <TableNodata message="No Interviews" description="You have no interviews scheduled." />
        </Box>
      ) : (
        data.map((item) => {
          const scheduledAt = dayjs(item.scheduled_at);
          const canJoin = item.meeting_url && ["SCHEDULED", "RESCHEDULED"].includes(String(item.status).toUpperCase());
          return (
            <Box key={item.interview_id} sx={rowSx}>
              <Avatar sx={{ ...commonAvatarStyle, width: 32, height: 32, fontSize: 12, mt: 0.25, flexShrink: 0 }}>
                {initials(item.interviewer_name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Interviewer + date pill */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <TruncatedText onlyWhenTruncated text={`Interview with ${item.interviewer_name}`} sx={{ fontWeight: 600, fontSize: "14px" }} />
                  {scheduledAt.isValid() && <Box sx={datePillSx}>{scheduledAt.format("MMMM D")}</Box>}
                </Box>
                {/* Job role */}
                {item.job_role && (
                  <TruncatedText onlyWhenTruncated text={item.job_role} sx={{ fontSize: "12px", color: "#1e88e5", fontWeight: 500 }} />
                )}
                {/* Time · duration + status / join */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 0.25 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", minWidth: 0 }} noWrap>
                    {scheduledAt.isValid() && (
                      <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                        {scheduledAt.format("hh:mm A")}
                      </Box>
                    )}
                    {item.duration_minutes ? ` · ${item.duration_minutes} Minutes` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Chip
                      label={formatStatus(item.status)}
                      size="small"
                      color={interviewChipColor(item.status)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "11px", fontWeight: 600 }}
                    />
                    {canJoin && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VideocamOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                        href={meetingHref(item.meeting_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: "none", fontWeight: 600, height: 26 }}
                      >
                        Join
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  </Card>
);

/* ---------------- Data hooks ---------------- */

// GET /student/dashboard/summary: counts + my_assessments + my_interviews for the logged-in candidate
const useStudentSummary = () => {
  const { showSnackbar } = useSnackbar();
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await api.get("/student/dashboard/summary");
        if (!ignore) setSummary(res?.data?.status === "success" ? res.data.data || {} : {});
      } catch (error) {
        if (!ignore) {
          setSummary({});
          showApiError(showSnackbar, error, "Failed to load your dashboard. Please try again.");
        }
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      ignore = true;
    };
  }, [showSnackbar]);

  return { summary, loading };
};

/* ---------------- Page ---------------- */

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const examLabel = getLabel("EXAM");
  const firstName = localStorage.getItem("firstName") || "";

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const { summary, loading } = useStudentSummary();
  // Soonest first
  const myAssessments = [...(summary.my_assessments || [])].sort((a, b) =>
    `${a.scheduled_date} ${a.scheduled_time}`.localeCompare(`${b.scheduled_date} ${b.scheduled_time}`)
  );
  const myInterviews = [...(summary.my_interviews || [])].sort((a, b) => dayjs(a.scheduled_at).diff(dayjs(b.scheduled_at)));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700} sx={{ color: "#0a0a0a" }}>
          Hi {firstName}
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "stretch", flexWrap: "wrap", gap: 1, mb: 3 }}>
        <KpiCard
          show={loaded}
          delay={300}
          loading={loading}
          label={`Open ${examLabel}s`}
          value={summary.open_assessments}
          image={test}
        />
        <KpiCard
          show={loaded}
          delay={400}
          loading={loading}
          label={`Completed ${examLabel}s`}
          value={summary.completed_assessments}
          image={exam}
        />
        <KpiCard
          show={loaded}
          delay={500}
          loading={loading}
          label="Scheduled Interviews"
          value={summary.scheduled_interviews}
          image={interviewIcon}
        />
      </Box>

      {/* My assessments + my interviews */}
      <SectionTitle icon={<AssignmentOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />} title="My Overview" />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3 }}>
        <MyAssessments
          data={myAssessments}
          loading={loading}
          examLabel={examLabel}
          onOpenAssessments={() => navigate("/assesment")}
          onOpenResults={() => navigate("/examHistory")}
        />
        <MyInterviews data={myInterviews} loading={loading} />
      </Box>
    </Box>
  );
};

export default CandidateDashboard;
